import React, { useEffect, useImperativeHandle, useRef, useState } from 'react';
import { ResponsivePie } from '@nivo/pie';
import { PopupLayout } from "../../../../PopupLayout/PopupLayout";

/**
 * Returns a formatted equipment name that includes the unit number.
 * Mirrors the behavior in your OPEX graph.
 */
const getUniqueEquipmentName = (equipment) => {
    const unitNumber = equipment.prop('unitNumber');
    const unitPrefix = unitNumber ? `Unit ${unitNumber} ` : '';
    
    let baseName = equipment.attributes.attrs.shapename?.innerHTML || 'Equipment';
    // Remove HTML tags like <p>, </p>, and <br>
    baseName = baseName.replace(/<p>|<\/p>|<br>/g, '').trim();
    
    if (!baseName) {
        baseName = equipment.attributes.unitType || 'Equipment';
    }
    return `${unitPrefix}${baseName}`;
};

const CapexGraphPopup = (props, ref) => {
    const [data, setData] = useState([]);
    const [update, setState] = useState(false);
    // Toggle to show/hide the Installation Costs slice.
    // When hidden, show all equipment items individually.
    const [showInstallation, setShowInstallation] = useState(true);
    const forceUpdate = React.useCallback(() => setState(s => !s), []);
    const componentRef = useRef(null);

    // Expose forceUpdate to parent components.
    useImperativeHandle(ref, () => ({
        forceUpdate: () => {
            forceUpdate();
        }
    }));

    // Recalculate graph data when the popup is open, when update toggles, or when the installation toggle changes.
    useEffect(() => {
        if (props.isOpen) {
            setGraphSettings();
        }
    }, [props.isOpen, update, showInstallation]);

    /**
     * Build the CAPEX breakdown data:
     * - Collect each equipment’s CAPEX (with unit numbers in the label).
     * - If installation costs are shown:
     *     • Limit the number of individual equipment slices using a significance threshold and maximum count.
     *     • Group the remainder as "Other Equipment".
     *     • Also add the Installation Costs slice.
     * - If installation is hidden:
     *     • Show all equipment items individually.
     * - In both cases, add Additional Capex if present.
     *
     * Installation Costs are computed as:
     *      totalCapexInstalled - totalEquipmentCapex - additionalCapex.
     */
    const setGraphSettings = () => {
        const graph = props.getEconArrays[1]; // JointJS graph with equipment elements.
        const econVars = props.getEconArrays[3] || {};

        const totalCapexInstalled = parseFloat(econVars.totalCapex || 0);
        const additionalCapex = parseFloat(econVars.additionalCapex || 0);

        // Retrieve equipment elements from the graph.
        const allEquipment = typeof graph.getElements === "function" ? graph.getElements() : [];

        // 1) Collect CAPEX data for each equipment.
        let equipmentItems = [];
        let totalEquipmentCapex = 0;

        allEquipment.forEach((equipment) => {
            if (
                equipment.prop("isUnit") === "yes" &&
                equipment.attributes?.equipment_Properties?.unitType !== 'feed' &&
                equipment.attributes?.equipment_Properties?.unitType !== 'Feed' &&
                equipment.attributes?.equipment_Properties?.unitType !== 'product' &&
                equipment.attributes?.equipment_Properties?.unitType !== 'Product'
            ) {
                const capexValue = parseFloat(equipment.attributes?.equipment_Economics?.Capex?.Value || 0);
                if (capexValue > 0.01) {
                    const equipName = getUniqueEquipmentName(equipment);
                    equipmentItems.push({
                        id: equipName,
                        label: equipName,
                        value: capexValue
                    });
                    totalEquipmentCapex += capexValue;
                }
            }
        });

        // Sort equipment items by descending CAPEX value.
        equipmentItems.sort((a, b) => b.value - a.value);

        let newData = [];
        if (showInstallation) {
            // When installation costs are shown, group equipment slices:
            const MAX_EQUIP_ITEMS = 6;
            // Define a significance threshold (here, 1% of the total installed CAPEX)
            const SIGNIFICANCE_THRESHOLD = totalCapexInstalled * 0.01;
            let significantEquipment = equipmentItems.filter(item => item.value >= SIGNIFICANCE_THRESHOLD);
            if (significantEquipment.length > MAX_EQUIP_ITEMS) {
                significantEquipment = significantEquipment.slice(0, MAX_EQUIP_ITEMS);
            }
            const otherEquipment = equipmentItems.filter(item => 
                !significantEquipment.some(sig => sig.id === item.id)
            );
            const otherSum = otherEquipment.reduce((sum, item) => sum + item.value, 0);
            newData = [...significantEquipment];
            if (otherSum > 0.01) {
                newData.push({
                    id: "Other Equipment",
                    label: "Other Equipment",
                    value: otherSum
                });
            }
            // Add the Installation Costs slice.
            const installationCosts = totalCapexInstalled - totalEquipmentCapex - additionalCapex;
            if (installationCosts > 0.01) {
                newData.push({
                    id: "Installation Costs",
                    label: "Installation Costs",
                    value: installationCosts
                });
            }
        } else {
            // When installation is hidden, show every equipment individually.
            newData = equipmentItems.slice();
        }

        // Add Additional Capex regardless of installation toggle.
        if (additionalCapex > 0.01) {
            newData.push({
                id: "Additional Capex",
                label: "Additional Capex",
                value: additionalCapex
            });
        }

        // Fallback if no CAPEX slices are available.
        if (newData.length === 0 && totalCapexInstalled > 0.01) {
            newData.push({
                id: "Base Capital Costs",
                label: "Base Capital Costs",
                value: totalCapexInstalled
            });
        }

        // Sort final data in descending order.
        newData.sort((a, b) => b.value - a.value);

        // Force "Installation Costs" slice (if any) to be last (on top).
        const installationIndex = newData.findIndex(item => item.id === "Installation Costs");
        if (installationIndex !== -1) {
            const [installationSlice] = newData.splice(installationIndex, 1);
            newData.push(installationSlice);
        }

        // Assign a consistent color scheme.
        const colorScheme = [
            '#9e0142', '#d53e4f', '#f46d43', '#fdae61', '#fee08b',
            '#e6f598', '#abdda4', '#66c2a5', '#3288bd', '#5e4fa2'
        ];
        newData.forEach((item, index) => {
            item.color = colorScheme[index % colorScheme.length];
        });

        setData(newData);
    };

    return (
        <>
            {props.isOpen && (
                <PopupLayout 
                    componentRef={componentRef}
                    pageX={window.innerWidth / 2}
                    pageY={window.innerHeight / 2}
                    minWidth="750px"
                    title="Capex Graph"
                    closeFunction={props.onClose}
                    overflow={false}
                >
                    <div style={{ width: '730px' }}>
                        {/* Display Total CAPEX */}
                        <div style={{ 
                            textAlign: 'center', 
                            marginBottom: '10px', 
                            fontWeight: 'bold', 
                            fontSize: '16px'
                        }}>
                            Total CAPEX: $
                            {props.getEconArrays[3]?.totalCapex
                                ? Math.round(parseFloat(props.getEconArrays[3].totalCapex)).toLocaleString('en-US')
                                : '0'}
                        </div>

                        {/* Toggle for showing/hiding Installation Costs */}
                        <div style={{ textAlign: 'right', marginBottom: '5px' }}>
                            <label style={{ fontSize: '14px' }}>
                                <input 
                                    type="checkbox"
                                    checked={showInstallation}
                                    onChange={(e) => setShowInstallation(e.target.checked)}
                                    style={{ marginRight: '5px' }}
                                />
                                Show Installation Costs
                            </label>
                        </div>

                        {data.length > 0 ? (
                            <div style={{ height: '450px', position: 'relative' }}>
                                <ResponsivePie
                                    data={data}
                                    /* Increase right margin to provide more room for labels. */
                                    margin={{ top: 40, right: 260, bottom: 150, left: 220 }}
                                    innerRadius={0.4}
                                    padAngle={1.0}  // Add space between slices to reduce overlap
                                    cornerRadius={3}
                                    colors={d => d.data.color || '#666'}
                                    borderWidth={1}
                                    borderColor={{ from: 'color', modifiers: [['darker', 0.2]] }}
                                    enableArcLabels={false}
                                    /* Increase label geometry to give more breathing room for text. */
                                    arcLinkLabelsSkipAngle={10}
                                    arcLinkLabelsStraightLength={20}
                                    arcLinkLabelsDiagonalLength={40}
                                    arcLinkLabelsTextOffset={10}
                                    arcLinkLabelsThickness={2}
                                    arcLinkLabelsColor={{ from: 'color' }}
                                    arcLinkLabel={d => {
                                        const total = data.reduce((sum, item) => sum + item.value, 0);
                                        const percentage = (d.value / total * 100).toFixed(1);
                                        return percentage < 1
                                            ? `${d.id}: ${percentage}%`
                                            : `${d.id}: $${Math.round(d.value).toLocaleString('en-US')} (${percentage}%)`;
                                    }}
                                    valueFormat={value => `$${Math.round(value).toLocaleString('en-US')}`}
                                    /* No built-in legend; we use a custom legend below. */
                                    legends={[]}
                                    /* No spinning animation, so labels remain stable. */
                                    animate={false}
                                    tooltip={({ datum }) => (
                                        <div style={{
                                            background: 'white',
                                            padding: '9px 12px',
                                            border: '1px solid #ccc',
                                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                                            borderRadius: '4px',
                                            maxWidth: '280px'
                                        }}>
                                            <div style={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                marginBottom: '5px'
                                            }}>
                                                <div style={{
                                                    width: '12px',
                                                    height: '12px',
                                                    backgroundColor: datum.color,
                                                    marginRight: '8px'
                                                }} />
                                                <strong style={{
                                                    whiteSpace: 'normal',
                                                    wordWrap: 'break-word'
                                                }}>{datum.id}</strong>
                                            </div>
                                            <div style={{ marginLeft: '20px' }}>
                                                <span>
                                                    <strong>${Math.round(datum.value).toLocaleString('en-US')}</strong>
                                                </span>
                                                <div style={{ fontSize: '0.9em', marginTop: '3px' }}>
                                                    {((datum.value / data.reduce((sum, item) => sum + item.value, 0)) * 100).toFixed(1)}% of total
                                                </div>
                                            </div>
                                        </div>
                                    )}
                                />
                            </div>
                        ) : (
                            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '450px' }}>
                                <p>No CAPEX data available</p>
                            </div>
                        )}

                        {/* Custom Legend (mirroring the OPEX graph approach) */}
                        <div style={{ 
                            display: 'flex',
                            flexWrap: 'wrap',
                            justifyContent: 'center',
                            marginTop: '5px',
                            padding: '0 10px',
                            maxHeight: '80px',
                            overflowY: 'auto'
                        }}>
                            {data.map((item, index) => (
                                <div 
                                    key={index} 
                                    style={{ 
                                        display: 'flex', 
                                        alignItems: 'center', 
                                        margin: '4px 8px',
                                        width: '140px'
                                    }}
                                >
                                    <div style={{
                                        width: '10px',
                                        height: '10px',
                                        backgroundColor: item.color,
                                        borderRadius: '50%',
                                        marginRight: '8px'
                                    }} />
                                    <span style={{ 
                                        fontSize: '12px', 
                                        whiteSpace: 'nowrap', 
                                        overflow: 'hidden', 
                                        textOverflow: 'ellipsis'
                                    }}>
                                        {item.id}
                                    </span>
                                </div>
                            ))}
                        </div>
                    </div>
                </PopupLayout>
            )}
        </>
    );
};

export default React.forwardRef((props, ref) => CapexGraphPopup(props, ref));
